import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import { categories, articles } from '@/mocks/data';
import type { Article, Category } from '@/mocks/types';

const articleImages: Record<string, string> = {
  'article-01': 'https://readdy.ai/api/search-image?query=A%20clean%20modern%20minimalist%20workspace%20scene%20with%20a%20smartphone%20displaying%20app%20interface%20on%20a%20warm%20wooden%20desk%2C%20soft%20natural%20light%20from%20window%2C%20cream%20and%20beige%20tones%2C%20editorial%20product%20photography%2C%20high%20detail%2C%20harmonious%20composition&width=800&height=500&seq=article-01&orientation=landscape',
  'article-02': 'https://readdy.ai/api/search-image?query=A%20modern%20iPhone%20on%20a%20minimalist%20white%20desk%20with%20a%20coffee%20cup%2C%20bright%20natural%20window%20light%2C%20clean%20cream%20background%2C%20editorial%20product%20photography%2C%20warm%20ambient%20tones&width=800&height=500&seq=article-02&orientation=landscape',
  'article-03': 'https://readdy.ai/api/search-image?query=A%20futuristic%20AI%20concept%20visualization%20with%20glowing%20amber%20data%20nodes%20on%20a%20dark%20minimalist%20background%2C%20abstract%20network%20connections%2C%20clean%20tech%20aesthetic%2C%20warm%20golden%20accents&width=800&height=500&seq=article-03&orientation=landscape',
  'article-04': 'https://readdy.ai/api/search-image?query=A%20closeup%20of%20hands%20typing%20on%20a%20laptop%20keyboard%20with%20a%20phone%20nearby%2C%20warm%20cozy%20lighting%2C%20cream%20and%20beige%20color%20palette%2C%20minimalist%20workspace%2C%20soft%20shadows&width=800&height=500&seq=article-04&orientation=landscape',
  'article-05': 'https://readdy.ai/api/search-image?query=A%20clean%20shield%20icon%20on%20a%20smartphone%20screen%20with%20soft%20glow%2C%20representing%20security%20and%20protection%2C%20warm%20minimalist%20setting%2C%20cream%20background%2C%20professional%20tech%20photography&width=800&height=500&seq=article-05&orientation=landscape',
  'article-06': 'https://readdy.ai/api/search-image?query=A%20modern%20app%20update%20notification%20concept%20on%20a%20smartphone%20screen%2C%20clean%20minimalist%20design%2C%20warm%20amber%20accents%2C%20soft%20natural%20light%2C%20cream%20background%2C%20professional%20UI%20mockup&width=800&height=500&seq=article-06&orientation=landscape',
  'article-07': 'https://readdy.ai/api/search-image?query=A%20minimalist%20privacy%20lock%20concept%20on%20a%20smartphone%2C%20soft%20golden%20glow%2C%20warm%20beige%20background%2C%20clean%20tech%20design%2C%20professional%20product%20photography%2C%20elegant%20shadows&width=800&height=500&seq=article-07&orientation=landscape',
  'article-08': 'https://readdy.ai/api/search-image?query=Multiple%20devices%20phone%20tablet%20laptop%20displayed%20on%20a%20clean%20wooden%20desk%2C%20warm%20natural%20light%2C%20cream%20minimalist%20background%2C%20synchronization%20concept%2C%20professional%20tech%20photography&width=800&height=500&seq=article-08&orientation=landscape',
  'article-09': 'https://readdy.ai/api/search-image?query=A%20premium%20golden%20membership%20card%20on%20a%20warm%20minimalist%20background%2C%20amber%20accents%2C%20soft%20lighting%2C%20clean%20elegant%20design%2C%20professional%20product%20photography%2C%20luxurious%20feel&width=800&height=500&seq=article-09&orientation=landscape',
  'article-10': 'https://readdy.ai/api/search-image?query=A%20wifi%20signal%20icon%20glowing%20above%20a%20smartphone%20on%20a%20minimalist%20desk%2C%20warm%20lighting%2C%20cream%20background%2C%20clean%20tech%20aesthetic%2C%20connection%20concept%2C%20professional%20photography&width=800&height=500&seq=article-10&orientation=landscape',
  'article-11': 'https://readdy.ai/api/search-image?query=A%20smartphone%20displaying%20dark%20mode%20interface%20in%20a%20dimly%20lit%20cozy%20room%2C%20warm%20ambient%20light%2C%20minimalist%20setting%2C%20comfortable%20atmosphere%2C%20professional%20tech%20photography&width=800&height=500&seq=article-11&orientation=landscape',
  'article-12': 'https://readdy.ai/api/search-image?query=A%20beginner%20friendly%20smartphone%20tutorial%20concept%20with%20welcome%20screen%20on%20phone%2C%20warm%20inviting%20atmosphere%2C%20cream%20background%2C%20soft%20natural%20light%2C%20clean%20minimalist%20design&width=800&height=500&seq=article-12&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const currentCategory: Category | undefined = categories.find((c) => c.slug === slug);
  const categoryArticles: Article[] = articles.filter((a) => a.category === slug);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://kuaimaoapp.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://kuaimaoapp.com/category/${slug}` },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 快猫app`,
    description: currentCategory.description,
    url: `https://kuaimaoapp.com/category/${slug}`,
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-24 pb-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{currentCategory.name}</span>
          </nav>

          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center">
                <i className={`${currentCategory.icon} text-2xl text-accent-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  {currentCategory.description}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group rounded-2xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={articleImages[article.imageSeq]}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs text-foreground-500">{article.date}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-700 transition-colors duration-300">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-16">
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTopButton />
    </>
  );
}